import { useEffect, useRef } from 'react';
import { Subscription } from '@rails/actioncable';
import { useActionCable } from '../providers/ActionCableProvider';

export interface BookingData {
  type: 'received_booking';
  message: string;
}

/**
 * Hook to subscribe to booking notifications via Action Cable.
 * Replaces Pusher channel: private-{userId}
 * Used by tutors to receive real-time booking notifications.
 *
 * @param userId - The user ID (tutor) to subscribe for
 * @param onBooking - Callback when a new booking notification is received
 */
export const useBookingChannel = (
  userId: string | null | undefined,
  onBooking: (data: BookingData) => void,
) => {
  const { subscribe, unsubscribe, connected } = useActionCable();
  const subscriptionRef = useRef<Subscription | null>(null);
  const onBookingRef = useRef(onBooking);

  // Keep callback ref updated
  useEffect(() => {
    onBookingRef.current = onBooking;
  }, [onBooking]);

  useEffect(() => {
    if (!userId || !connected) return;

    subscriptionRef.current = subscribe(
      'BookingChannel',
      {},
      {
        received: (data: BookingData) => {
          if (data.type === 'received_booking') {
            onBookingRef.current(data);
          }
        },
      },
    );

    return () => {
      if (subscriptionRef.current) {
        unsubscribe(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [userId, connected, subscribe, unsubscribe]);
};

export default useBookingChannel;
